import { useState } from "react";

interface Product {
  id: number;
  name: string;
  salePrice: string;
  image: string;
  supplier: string;
  shipping: string;
}

interface Props {
  product: Product;
  buyerName: string;
  onClose: () => void;
}

type Step = "details" | "confirm" | "success";

export default function BuyerOrderModal({ product, buyerName, onClose }: Props) {
  const [step, setStep] = useState<Step>("details");
  const [qty, setQty] = useState(1);
  const [form, setForm] = useState({
    address: "",
    city: "",
    country: "",
    zip: "",
    cardName: "",
    cardNumber: "",
    expiry: "",
    cvv: "",
  });
  const [placing, setPlacing] = useState(false);

  const price = parseFloat(product.salePrice.replace("$", ""));
  const subtotal = price * qty;
  const shipping = 4.99;
  const total = subtotal + shipping;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handlePlaceOrder = (e: React.FormEvent) => {
    e.preventDefault();
    setPlacing(true);
    setTimeout(() => {
      setPlacing(false);
      setStep("success");
    }, 1400);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-white font-bold text-lg">Place Order</h2>
            <p className="text-white/80 text-sm">Hi {buyerName}!</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="p-6">
          {step === "success" ? (
            <div className="text-center py-8">
              <div className="w-20 h-20 flex items-center justify-center rounded-full bg-green-100 mx-auto mb-4">
                <i className="ri-truck-line text-green-500 text-4xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 text-xl mb-2">Order Placed!</h3>
              <p className="text-gray-500 text-sm mb-1">Your order has been confirmed.</p>
              <p className="text-gray-500 text-sm mb-4">
                Estimated delivery: <strong>{product.shipping}</strong>
              </p>
              <div className="bg-stone-50 rounded-xl p-4 text-left mb-5 border border-stone-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg overflow-hidden flex-shrink-0">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 text-sm">{product.name}</div>
                    <div className="text-stone-400 text-xs">Qty: {qty} · Total: ${total.toFixed(2)}</div>
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                Continue Shopping
              </button>
            </div>
          ) : step === "details" ? (
            <>
              {/* Product Summary */}
              <div className="flex items-center gap-3 bg-stone-50 rounded-xl p-3 mb-5 border border-stone-100">
                <div className="w-14 h-14 flex items-center justify-center rounded-lg overflow-hidden flex-shrink-0">
                  <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-900 text-sm truncate">{product.name}</div>
                  <div className="text-stone-400 text-xs">{product.supplier}</div>
                  <div className="text-amber-600 font-bold text-sm mt-0.5">{product.salePrice}</div>
                </div>
              </div>

              {/* Quantity */}
              <div className="mb-5">
                <label className="block text-xs font-semibold text-gray-600 mb-2">Quantity</label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setQty((q) => Math.max(1, q - 1))}
                    className="w-9 h-9 flex items-center justify-center rounded-lg border border-stone-200 text-stone-600 hover:border-amber-400 hover:text-amber-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-subtract-line"></i>
                  </button>
                  <span className="font-bold text-gray-900 text-lg w-8 text-center">{qty}</span>
                  <button
                    type="button"
                    onClick={() => setQty((q) => q + 1)}
                    className="w-9 h-9 flex items-center justify-center rounded-lg border border-stone-200 text-stone-600 hover:border-amber-400 hover:text-amber-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                  <div className="ml-auto text-right">
                    <div className="text-xs text-stone-400">Subtotal</div>
                    <div className="font-bold text-gray-900">${subtotal.toFixed(2)}</div>
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="space-y-3 mb-5">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Shipping Address</p>
                <input
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  placeholder="Street address"
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                />
                <div className="grid grid-cols-2 gap-3">
                  <input
                    name="city"
                    value={form.city}
                    onChange={handleChange}
                    placeholder="City"
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                  />
                  <input
                    name="zip"
                    value={form.zip}
                    onChange={handleChange}
                    placeholder="ZIP / Postal"
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
                <select
                  name="country"
                  value={form.country}
                  onChange={handleChange}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400 transition-colors bg-white cursor-pointer"
                >
                  <option value="">Select country</option>
                  <option>United States</option>
                  <option>Canada</option>
                  <option>United Kingdom</option>
                  <option>Australia</option>
                  <option>Germany</option>
                  <option>France</option>
                  <option>Japan</option>
                  <option>Singapore</option>
                  <option>UAE</option>
                  <option>Other</option>
                </select>
              </div>

              {/* Order Summary */}
              <div className="bg-stone-50 rounded-xl p-4 mb-5 border border-stone-100 space-y-2">
                <div className="flex justify-between text-sm text-stone-500">
                  <span>Subtotal ({qty} item{qty > 1 ? "s" : ""})</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-stone-500">
                  <span>Shipping</span>
                  <span>${shipping.toFixed(2)}</span>
                </div>
                <div className="border-t border-stone-200 pt-2 flex justify-between font-bold text-gray-900">
                  <span>Total</span>
                  <span>${total.toFixed(2)}</span>
                </div>
              </div>

              <button
                onClick={() => setStep("confirm")}
                className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                Continue to Payment
              </button>
            </>
          ) : (
            <form onSubmit={handlePlaceOrder} className="space-y-4">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-3">Payment Details</p>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Name on Card</label>
                <input
                  name="cardName"
                  value={form.cardName}
                  onChange={handleChange}
                  required
                  placeholder="John Smith"
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Card Number</label>
                <input
                  name="cardNumber"
                  value={form.cardNumber}
                  onChange={handleChange}
                  required
                  placeholder="1234 5678 9012 3456"
                  maxLength={19}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Expiry</label>
                  <input
                    name="expiry"
                    value={form.expiry}
                    onChange={handleChange}
                    required
                    placeholder="MM/YY"
                    maxLength={5}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">CVV</label>
                  <input
                    name="cvv"
                    value={form.cvv}
                    onChange={handleChange}
                    required
                    placeholder="123"
                    maxLength={4}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
              </div>

              <div className="bg-amber-50 rounded-xl p-3 border border-amber-100 flex justify-between items-center">
                <span className="text-amber-700 font-semibold text-sm">Total to Pay</span>
                <span className="text-amber-700 font-bold text-lg">${total.toFixed(2)}</span>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep("details")}
                  className="flex-1 border border-stone-200 text-stone-600 hover:bg-stone-50 font-semibold py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={placing}
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm disabled:opacity-60"
                >
                  {placing ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      Placing...
                    </span>
                  ) : "Place Order"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
